import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  message,
  Alert,
  Space,
  Typography,
  Row,
  Col,
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { usePayrollProcessing } from "../store/context";
import { PayrollPeriod } from "../../../types/hr";
import { validatePeriodComplete, generatePeriodName } from "../utils";
import { useSelection } from "../../../SelectionContext";

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface PeriodFormModalProps {
  visible: boolean;
  period?: PayrollPeriod | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const PeriodFormModal: React.FC<PeriodFormModalProps> = ({
  visible,
  period,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { createPeriod, updatePeriod } = usePayrollProcessing();
  const { selectedCompany } = useSelection();
  const [loading, setLoading] = useState(false);
  const [autoGenerateName, setAutoGenerateName] = useState(true);

  const isEditing = !!period;

  useEffect(() => {
    if (visible) {
      if (period) {
        // Mode édition
        form.setFieldsValue({
          name: period.name,
          dateRange: [dayjs(period.start_date), dayjs(period.end_date)],
          description: period.description || "",
        });
        setAutoGenerateName(false);
      } else {
        // Mode création
        form.resetFields();
        setAutoGenerateName(true);
      }
    }
  }, [visible, period, form]);

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1] && autoGenerateName) {
      const generatedName = generatePeriodName(
        dates[0].toDate(),
        dates[1].toDate()
      );
      form.setFieldValue("name", generatedName);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Si l'utilisateur modifie manuellement le nom, on désactive la génération automatique
    setAutoGenerateName(false);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const { name, dateRange, description } = values;
      const [startDate, endDate] = dateRange;

      // Validation
      const validation = validatePeriodComplete(
        name,
        startDate.toDate(),
        endDate.toDate()
      );

      if (!validation.isValid) {
        message.error(validation.errors[0]);
        return;
      }

      const periodData = {
        name: name.trim(),
        year: startDate.year(),
        month: startDate.month() + 1, // dayjs month is 0-indexed
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
        company_id: selectedCompany,
        description: description?.trim() || null,
      };

      if (isEditing && period?.id) {
        await updatePeriod(period.id, periodData);
        message.success("Période modifiée avec succès");
      } else {
        await createPeriod(periodData);
        message.success("Période créée avec succès");
      }

      onSuccess();
    } catch (error: any) {
      console.error("Error saving period:", error);
      const conflicts = error?.details?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length > 0) {
        const firstConflict = conflicts[0];
        message.error(
          `Conflit avec la période "${firstConflict.period_name}" (${firstConflict.start_date} - ${firstConflict.end_date})`
        );
      } else {
        message.error(error.message || "Erreur lors de la sauvegarde");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          {isEditing ? <EditOutlined /> : <PlusOutlined />}
          <Title level={4} style={{ margin: 0 }}>
            {isEditing ? "Modifier la Période" : "Nouvelle Période de Paie"}
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Alert
          message={
            isEditing
              ? "Modification d'une période existante"
              : "Création d'une nouvelle période de paie"
          }
          description={
            isEditing
              ? 'Vous ne pouvez modifier que les périodes en statut "Brouillon".'
              : "Vous pouvez aussi créer une période passée pour un rattrapage. Ajoutez ensuite les employés et leurs variables de paie."
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Nom de la Période"
              name="name"
              rules={[
                { required: true, message: "Le nom est requis" },
                {
                  min: 3,
                  message: "Le nom doit contenir au moins 3 caractères",
                },
                {
                  max: 100,
                  message: "Le nom ne peut pas dépasser 100 caractères",
                },
              ]}
            >
              <Input
                placeholder="Ex: Janvier 2024, Salaires du mois..."
                onChange={handleNameChange}
                suffix={
                  autoGenerateName && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Auto
                    </Text>
                  )
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Période de Paie"
              name="dateRange"
              rules={[{ required: true, message: "La période est requise" }]}
            >
              <RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder={["Date de début", "Date de fin"]}
                onChange={handleDateRangeChange}
                suffixIcon={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Description (optionnelle)" name="description">
              <TextArea
                rows={3}
                placeholder="Description de la période, notes particulières..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        {!isEditing && (
          <Alert
            message="Prochaines étapes"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Ajouter les employés à inclure dans cette période</li>
                <li>Saisir les variables de paie (présences, primes, etc.)</li>
                <li>Lancer les calculs de paie</li>
                <li>Valider et générer les bulletins</li>
              </ul>
            }
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Row justify="end" gutter={16}>
          <Col>
            <Button onClick={onCancel}>Annuler</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={isEditing ? <EditOutlined /> : <PlusOutlined />}
            >
              {isEditing ? "Modifier" : "Créer"} la Période
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PeriodFormModal;
